import { useMemo } from 'react';
import { PALETTE, minutesToHHMM } from '@micplot/shared';
import {
  useStore, usePresenters, useChannels, useAssignments, useTelemetry,
} from '../state/store';
import ConflictPanel from '../components/ConflictPanel';
import RfMeter from '../components/RfMeter';
import { useNowMinutes } from '../lib/now';

/**
 * The A1 view: front-of-house wants a stable, channel-ordered list of what is
 * live on each transmitter right now, not a drag-and-drop surface. Read-only by
 * intent -- the A1 is mixing, not shuffling assignments.
 */
export default function A1View() {
  const presenters = usePresenters();
  const channels = useChannels();
  const assignments = useAssignments();
  const conflicts = useStore(s => s.conflicts);
  const telemetry = useTelemetry();

  const byId = useMemo(() => new Map(presenters.map(p => [p.id, p])), [presenters]);
  // Ticking, so the desk's own view of "now" does not quietly go stale.
  const nowMin = useNowMinutes();

  /*
   * Battery and RF belong here more than anywhere. A1 is the position holding
   * the faders when a pack dies, and this view had no telemetry on it at all --
   * you had to leave the thing you were mixing on to find out.
   */
  const meterFor = useMemo(() => {
    const byFreq = new Map<string, typeof telemetry[number]>();
    for (const t of telemetry) {
      if (t.frequency !== undefined) byFreq.set(t.frequency.toFixed(3), t);
    }
    return (freq?: number) => freq === undefined ? undefined : byFreq.get(freq.toFixed(3));
  }, [telemetry]);

  return (
    <div className="panel col" style={{ height: '100%' }}>
      <div className="panel-head">
        <span className="panel-title">Channel list</span>
        <span className="count">{channels.length} transmitters</span>
        <div className="header-spacer" />
        <span className="muted" style={{ fontSize: 13 }}>
          Live at {minutesToHHMM(nowMin)}
        </span>
        <button className="btn sm no-print" onClick={() => window.print()}
                title="Print the channel plot for a paper backup at the desk">
          Print
        </button>
      </div>

      <div className="col-body">
        <ConflictPanel conflicts={conflicts} />

        {channels.length === 0 ? (
          <div className="empty">No channels configured.</div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {channels.map(ch => {
              const mine = assignments
                .filter(a => a.channelId === ch.id)
                .sort((a, b) => a.start - b.start);
              // An unscheduled assignment is live for the entire show.
              const live = mine.find(a => !a.scheduled
                || (a.start <= nowMin && nowMin < a.end));
              const next = mine.find(a => a.scheduled && a.start > nowMin);
              /*
               * Outside the scheduled window every channel read "— open —",
               * so at five o'clock on a show day, or any night the running
               * order slipped, the mix position saw an empty rig while the
               * plot was sitting right there. A channel with nobody on it
               * *now* is not the same as a channel with nobody on it at all,
               * and the difference matters most to whoever is patching.
               */
              const shown = live ?? next ?? mine.at(-1);
              const when: 'live' | 'next' | 'earlier' | null =
                live ? 'live' : next ? 'next' : shown ? 'earlier' : null;
              const p = shown ? byId.get(shown.presenterId) : undefined;
              const np = next && live ? byId.get(next.presenterId) : undefined;

              return (
                <div key={ch.id} className="stage-card"
                     style={{
                       // Only what is on air right now earns the colour.
                       borderLeftColor: p && when === 'live'
                         ? PALETTE[p.color % PALETTE.length] : 'var(--line)',
                       padding: 12,
                       opacity: when === 'live' || when === null ? 1 : .7,
                     }}>
                  <span className="ch-num" style={{ width: 64 }}>TX {ch.number}</span>
                  <span className="chip">{ch.micType}</span>
                  {ch.frequency && <span className="ch-freq">{ch.frequency.toFixed(3)} MHz</span>}

                  <span style={{ flex: 1, minWidth: 0 }}>
                    {p && shown ? (
                      <>
                        <div style={{ fontWeight: 700, fontSize: 17 }}>{p.name}</div>
                        <div className="stage-meta">
                          {p.title ? `${p.title} · ` : ''}
                          {when === 'live'
                            ? (shown.scheduled
                                ? `on until ${minutesToHHMM(shown.end)}`
                                : 'whole show')
                            : when === 'next'
                              ? `from ${minutesToHHMM(shown.start)}`
                              : `was on ${minutesToHHMM(shown.start)}–${minutesToHHMM(shown.end)}`}
                          {!shown.deployed && ' · staged'}
                        </div>
                      </>
                    ) : (
                      <span className="muted">— open —</span>
                    )}
                  </span>

                  {np && (
                    <span style={{ textAlign: 'right' }}>
                      <div className="stage-meta">next {minutesToHHMM(next!.start)}</div>
                      <div style={{ fontWeight: 600 }}>{np.name}</div>
                    </span>
                  )}

                  {meterFor(ch.frequency) && (
                    <RfMeter t={meterFor(ch.frequency)!} />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
